import axios from 'axios';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';

const API = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Promote a quote's `recurringPlan` onto the lead at approval time.
 *
 * Sprint 304 (Multi-Program Phase 2 closure): this helper is now the
 * SINGLE writer for quote-driven service-plan activation. It POSTs to
 * `/api/leads/{leadId}/enrollments`, which:
 *   • creates an authoritative enrollment doc,
 *   • seeds the first recurring visit (Visit #1) tagged with the new
 *     enrollmentId + recurringServiceId=enrollmentId, and
 *   • writes the legacy `lead.recurrence` mirror when no primary
 *     enrollment pointer exists yet, so downstream read surfaces keep
 *     working during the Phase 2→3 transition.
 *
 * Idempotency:
 *   • If the lead already has any active enrollment (checked via
 *     `lead.activeEnrollmentId` OR the legacy `lead.recurrence.active`),
 *     we bail out with reason=`already_active`. Quote re-approvals must
 *     not overwrite the existing program.
 *
 * Recurring rate (Option D):
 *   `quoteContext.serviceSubtotal` is forwarded to the backend as
 *   `pricingAmount`. The enrollment doc records this as an authoritative
 *   per-enrollment price.
 *
 * Billing lineage (Phase 1):
 *   `quoteContext.quoteId` is forwarded so the enrollment + its seeded
 *   Visit #1 (and all recurring children) retain the originating quote id,
 *   which the completion billing pipeline expects. Absent → null (no
 *   fabricated relationship).
 *
 * @param {string} companyId
 * @param {string} leadId
 * @param {object} recurringPlan   Plan object from the quote. Must have `.active === true`.
 * @param {object} [quoteContext]  Optional. { serviceSubtotal?: number, quoteId?: string }
 * @returns {{ activated: boolean, reason?: string, enrollmentId?: string, amountWritten?: boolean }}
 */
export async function activateServicePlanFromQuote(
  companyId,
  leadId,
  recurringPlan,
  quoteContext = {},
) {
  if (!companyId || !leadId || !recurringPlan?.active) {
    return { activated: false, reason: 'missing_args_or_inactive' };
  }

  // ── Guard: no overwrite of an existing active enrollment ────────────
  // Consult the lead directly (cheap, one doc read) so this works even
  // when the caller doesn't have LeadsContext loaded.
  try {
    const snap = await getDoc(doc(db, 'companies', companyId, 'leads', leadId));
    const lead = snap.exists() ? snap.data() : {};
    if (lead?.activeEnrollmentId) {
      return { activated: false, reason: 'already_active' };
    }
    if (lead?.recurrence?.active === true) {
      return { activated: false, reason: 'already_active' };
    }
  } catch (readErr) {
    // Read failure is non-fatal — the backend will still enforce
    // its own constraints. Continue.
    console.warn('[activateServicePlanFromQuote] Lead pre-read failed:', readErr);
  }

  // ── Recurring rate → pricingAmount on the enrollment ───────────────
  const amount = Number(quoteContext?.serviceSubtotal);
  const hasAmount = Number.isFinite(amount) && amount >= 0;

  // Convert legacy plan.unit ('days'/'weeks'/'months') to canonical singular
  // that the backend enrollment schema expects.
  const rawUnit = (recurringPlan.unit ?? 'day').toString().toLowerCase();
  const unit = rawUnit.replace(/s$/, '');

  const body = {
    servicePlanTemplateId: recurringPlan.servicePlanTemplateId ?? null,
    programName:           recurringPlan.planLabel ?? null,
    planLabel:             recurringPlan.planLabel ?? null,
    interval:              Number.parseInt(recurringPlan.interval, 10),
    unit,
    rollMode:              recurringPlan.rollMode ?? 'schedule',
    totalOccurrences:      recurringPlan.totalOccurrences ?? null,
    startDate:             recurringPlan.startDate ?? null,
    pricingAmount:         hasAmount ? Number(amount.toFixed(2)) : null,
    quoteId:               quoteContext?.quoteId ?? null,
    // Snapshot the tax terms in force when the recurring agreement was sold,
    // so per-visit invoices bill the correct tax-inclusive amount regardless
    // of later company setting changes.
    taxEnabled:            typeof quoteContext?.taxEnabled === 'boolean' ? quoteContext.taxEnabled : null,
    taxRate:               Number.isFinite(Number(quoteContext?.taxRate)) ? Number(quoteContext.taxRate) : null,
  };

  const token = await auth?.currentUser?.getIdToken?.();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const res = await axios.post(
    `${API}/api/leads/${leadId}/enrollments`,
    body,
    { headers },
  );

  const enr = res?.data?.enrollment || {};
  return {
    activated:      true,
    enrollmentId:   enr.enrollmentId || null,
    amountWritten:  hasAmount,
  };
}
